import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiFetch } from "@/lib/api.js";
import { useAuth } from "@/auth/useAuth.js";

// Must mirror the worker's POST /ai/draft response (ai-draft.ts:206). `ai_run_id`
// is "" when the ai_runs insert fails, so it is a plain string, not a uuid.
const DraftResponse = z.object({
  ai_run_id: z.string(),
  model_used: z.string(),
  fallback_used: z.boolean(),
  photos_processed: z.number(),
  suggestions_raw: z.number(),
  suggestions_kept: z.number(),
  flags: z.unknown().optional(),
  suggestions: z.array(z.unknown()).default([]),
});

/**
 * The Worker stores suggestions as ai_suggestions rows in Supabase. The admin
 * SPA reads them directly via Supabase REST through the API base URL, falling
 * back to a derived shape from the draft response. For the demo we re-fetch
 * from a thin /admin endpoint exposed by Plan 3 via the `audit-logs` style read
 * (the Worker route admin/ai-draft also returns counts; full list comes from
 * an upcoming endpoint or via the listing detail join).
 *
 * Since Plan 3 does not expose GET /ai-suggestions, the admin SPA uses
 * Supabase JS directly with the user's JWT — RLS scopes by org_id.
 */

const Suggestion = z.object({
  id: z.string().uuid(),
  field_key: z.string(),
  suggested_value: z.unknown().nullable(),
  confidence: z.number(),
  source_evidence: z.object({
    type: z.enum(["photo", "intake_note", "absent"]),
    media_id: z.string().uuid().optional(),
    note_excerpt: z.string().optional(),
    reasoning: z.string(),
  }),
  status: z.enum(["needs_confirmation", "accepted", "rejected", "superseded"]),
  created_at: z.string(),
});
export type Suggestion = z.infer<typeof Suggestion>;

const SuggestionsResponse = z.object({ suggestions: z.array(Suggestion) });

export function useAISuggestions(listingId: string | undefined) {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["ai_suggestions", listingId],
    enabled: !!token && !!listingId,
    queryFn: async (): Promise<Suggestion[]> => {
      // Served by the worker (RLS blocks a direct browser read of ai_suggestions).
      const res = await apiFetch(
        `/api/v1/admin/listings/${listingId!}/ai-suggestions`,
        token!,
        SuggestionsResponse,
      );
      return res.suggestions;
    },
  });
}

export function useRunAIDraft(listingId: string) {
  const qc = useQueryClient();
  const { session } = useAuth();
  const token = session?.access_token;
  return useMutation({
    mutationFn: () => {
      if (!token) throw new Error("not authenticated");
      return apiFetch(`/api/v1/admin/listings/${listingId}/ai/draft`, token, DraftResponse, {
        method: "POST", body: { force_regenerate: false },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai_suggestions", listingId] });
      void qc.invalidateQueries({ queryKey: ["listing", listingId] });
    },
  });
}
